import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import WBCModal from "../elements/WBCModal";
import { WBCPrimaryButton } from "../elements/WBCPrimaryButton";
import { BestSelect, Input } from "best-common-react";
import styled from "styled-components";
import EligibilityListApi from "../../httpClients/EligibilityListApi";
import { useAlert } from "../hooks/useAlert";
import { AlertConstants } from "best-common-react";
import WBCYearsContext from "../../contexts/WBCYearsContext";
import PlayerEligibilityContext from "../../contexts/PlayerEligibilityContext";
import useEligibilityReasonOptions from "../hooks/useEligibilityReasonOptions";

const BodyContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
  max-height: 500px;
  gap: 1rem;
`;
BodyContent.displayName = "BodyContent";

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TextboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FooterContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #ddd;
`;
FooterContent.displayName = "FooterContent";

const Label = styled.label`
  font-weight: bold;
  font-size: 0.9rem;
`;

const EligibilityReasonModal = ({ isOpen, setIsOpen, selectedPlayer }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [reasonComments, setReasonComments] = useState("");
  const [approving, setApproving] = useState(false);

  const { selectedYear } = useContext(WBCYearsContext);
  const playerEligibilityContext = useContext(PlayerEligibilityContext);
  const showAlert = useAlert();
  const { teamId } = playerEligibilityContext.state.team;

  // Use the custom hook for loading eligibility reason options
  const { options: reasonOptions, isLoading: isLoadingOptions } = useEligibilityReasonOptions(isOpen);

  const onApprove = (withoutReason = false) => {
    if (!approving && selectedPlayer) {
      setApproving(true);

      // Construct the request body to match backend expectations
      const approveRequest = withoutReason
        ? null
        : {
            reason: selectedReason?.value || null,
            reasonComment: selectedReason?.value?.requiresComment ? reasonComments : null
          };

      EligibilityListApi.approveProfileEligibility(
        selectedYear,
        selectedPlayer.fedTeamId,
        selectedPlayer.profileId,
        approveRequest
      )
        .then(() => {
          showAlert("Approved");
          EligibilityListApi.getPlayerEligibilityList(selectedYear, teamId)
            .then(response => {
              playerEligibilityContext.dispatch({ type: "setPlayers", players: response });
              setApproving(false);
              handleClose();
            })
            .catch(() => setApproving(false));
        })
        .catch(({ response }) => {
          setApproving(false);
          showAlert(response.data.message, AlertConstants.TYPES.DANGER);
        });
    }
  };

  const handleClose = () => {
    setSelectedReason(null);
    setReasonComments("");
    setIsOpen(false);
  };

  const isApproveAndSaveDisabled =
    !selectedReason || (selectedReason?.value?.requiresComment && !reasonComments.trim());

  return (
    <WBCModal isOpen={isOpen} toggleModal={handleClose} headerText="Eligibility Reason">
      <BodyContent>
        <DropdownContainer>
          <Label>Please select a reason for eligibility.</Label>
          <BestSelect
            name="eligibilityReason"
            value={selectedReason}
            options={reasonOptions}
            onChange={setSelectedReason}
            placeholder={isLoadingOptions ? "Loading reasons..." : "Select a reason..."}
            isDisabled={isLoadingOptions}
            clearable
          />
        </DropdownContainer>

        {selectedReason?.value?.requiresComment && (
          <TextboxContainer>
            <Label>Type reason for previous WBC eligibility. *</Label>
            <Input
              value={reasonComments}
              onChange={e => setReasonComments(e.target.value)}
              placeholder="Type reason for previous WBC eligibility."
              required
            />
          </TextboxContainer>
        )}
      </BodyContent>

      <FooterContent>
        <WBCPrimaryButton disabled={isApproveAndSaveDisabled || approving} onClick={() => onApprove(false)}>
          {approving ? "Approving..." : "Approve"}
        </WBCPrimaryButton>
        <WBCPrimaryButton
          styles={{ width: "250px" }}
          onClick={() => onApprove(true)}
          disabled={approving || !!selectedReason}
        >
          Approve Without Eligibility Reason
        </WBCPrimaryButton>
        <WBCPrimaryButton onClick={handleClose} disabled={approving}>
          Cancel
        </WBCPrimaryButton>
      </FooterContent>
    </WBCModal>
  );
};

EligibilityReasonModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  selectedPlayer: PropTypes.object
};

export default EligibilityReasonModal;
